import {
  useEffect,
  useRef,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {
  Box,
  Button,
  CircularProgress,
} from '@material-ui/core';
import SelectControl from '../SelectControl';
import {
  fetchRemoteRows,
  makeRemoteOptions,
  prepareRemoteRequest,
  selectionValues,
} from 'utils/remoteSelect';

function RemoteSelectControl(props) {
  const {
    name, value, settings = {}, context = {}, formValues = {},
    onChange, events = {}, disabled, error, helperText,
    setFormValue, onRemoteStateChange,
  } = props;
  const [state, setState] = useState({
    loading: true,
    options: [],
  });
  const [retry, setRetry] = useState(0);
  const previousRequest = useRef();
  const currentProps = useRef(props);
  currentProps.current = props;
  const prepared = prepareRemoteRequest(settings.source, {
    context,
    form: formValues,
  });
  const key = JSON.stringify({
    ...prepared,
    settings,
  });

  useEffect(() => {
    let active = true;
    const cancel = axios.CancelToken.source();
    const clearSelection = () => {
      const { current } = currentProps;
      const cleared = selectionValues(current, null);
      Object.entries(cleared).forEach(([path, next]) => {
        if (current.setFormValue) current.setFormValue(path, next, false);
        else if (path === current.name) current.onChange?.({
          target: {
            name: path,
            value: next,
          },
        });
      });
    };
    if (previousRequest.current !== undefined && previousRequest.current !== key) clearSelection();
    previousRequest.current = key;
    if (!prepared.request) {
      setState({
        loading: false,
        options: [],
        error: prepared.error,
        waiting: prepared.waiting,
        message: prepared.message,
      });
      return () => { active = false; cancel.cancel(); };
    }
    setState({
      loading: true,
      options: [],
    });
    fetchRemoteRows(settings.source, prepared.request, cancel.token)
      .then((rows) => {
        if (!active) return;
        const options = makeRemoteOptions(rows, settings);
        const selected = currentProps.current.value;
        if (selected !== '' && selected != null && !options.some((option) => String(option.id) === String(selected))) clearSelection();
        setState({
          loading: false,
          options,
        });
      })
      .catch((failure) => {
        if (active && !axios.isCancel(failure)) setState({
          loading: false,
          options: [],
          error: failure.message || 'Error al cargar las opciones',
        });
      });
    return () => { active = false; cancel.cancel('Selector actualizado'); };
  }, [key, retry]);

  const selected = state.options.find((option) => String(option.id) === String(value));
  const invalidSelection = value !== '' && value != null && !selected;
  useEffect(() => {
    onRemoteStateChange?.(name, {
      loading: state.loading,
      waiting: Boolean(state.waiting),
      error: state.error,
      invalidSelection,
    });
  }, [name, state, invalidSelection, onRemoteStateChange]);
  useEffect(() => () => currentProps.current.onRemoteStateChange?.(name, null), [name]);

  const handleChange = (event) => {
    const option = state.options.find((entry) => String(entry.id) === String(event.target.value));
    const values = selectionValues(props, option);
    onChange?.({
      target: {
        name,
        value: values[name],
      },
    });
    Object.entries(values).forEach(([path, next]) => {
      if (path !== name) setFormValue?.(path, next, false);
    });
    events.onChange?.({
      name,
      value: values[name],
    });
  };

  return (
    <Box>
      <SelectControl
        name={name}
        label={props.label}
        size={props.size}
        value={selected?.id ?? ''}
        onBlur={props.onBlur}
        onChange={handleChange}
        disabled={disabled || state.loading || state.waiting || Boolean(state.error)}
        error={Boolean(error || state.error || (invalidSelection && !state.loading))}
        helperText={state.error || state.message || helperText || (!state.loading && !state.options.length ? 'No hay opciones disponibles' : undefined)}
        settings={{
          ...settings,
          multiple: false,
          options: state.options.map(({ id, name: label }) => ({
            id,
            name: label,
          })),
        }}
      />
      {state.loading && <Box mt={1}><CircularProgress size={16} aria-label="Cargando opciones" /></Box>}
      {state.error && <Button size="small" onClick={() => setRetry((attempt) => attempt + 1)}>Reintentar</Button>}
    </Box>
  );
}

RemoteSelectControl.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.node,
  size: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
  settings: PropTypes.object,
  context: PropTypes.object,
  formValues: PropTypes.object,
  events: PropTypes.object,
  disabled: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  helperText: PropTypes.node,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  setFormValue: PropTypes.func,
  onRemoteStateChange: PropTypes.func,
};

export default RemoteSelectControl;
